import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { UserX, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AccountDeactivationProps {
  showCard?: boolean;
}

export const AccountDeactivation: React.FC<AccountDeactivationProps> = ({
  showCard = true
}) => {
  const [isDeactivating, setIsDeactivating] = useState(false);
  const { toast } = useToast();

  const handleDeactivateAccount = async () => {
    setIsDeactivating(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { error } = await supabase
        .from('profiles')
        .update({ is_active: false })
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Account Deactivated",
        description: "Your account has been deactivated. You can reactivate it anytime by signing in again.",
      });

      // Sign out the user after deactivation
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = '/auth';
      }, 2000);

    } catch (error) {
      console.error('Error deactivating account:', error);
      toast({
        title: "Error",
        description: "Failed to deactivate account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  const content = (
    <div className="space-y-4">
      <div className="flex items-start space-x-3">
        <UserX className="h-5 w-5 text-muted-foreground mt-0.5" />
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Deactivate Account</h4>
          <p className="text-xs text-muted-foreground">
            Temporarily deactivate your account. You can reactivate it anytime by signing in again.
          </p>
           <ul className="text-xs text-muted-foreground space-y-1">
             <li>• Your data will be preserved</li>
             <li>• Your tokens won't be lost</li>
             <li>• You'll be hidden from search results</li>
             <li>• Reactivate anytime by logging in</li>
           </ul>
        </div>
      </div>
      
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" size="sm" className="w-full">
            Deactivate Account
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Confirm Account Deactivation
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to deactivate your account?</p>
              <div className="bg-muted p-3 rounded-md">
                <p className="text-sm font-medium mb-2">What happens when you deactivate:</p>
                 <ul className="text-sm space-y-1">
                   <li>• Your profile will be hidden from other users</li>
                   <li>• You'll be signed out automatically</li>
                   <li>• All your data remains safe and secure</li>
                   <li>• Your tokens will be preserved</li>
                   <li>• You can reactivate anytime by signing in</li>
                 </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline">Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeactivateAccount}
              disabled={isDeactivating}
            >
              {isDeactivating ? "Deactivating..." : "Yes, Deactivate"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card className="obsidian-card border-destructive/20">
      <CardHeader>
        <CardTitle className="text-destructive flex items-center gap-2">
          <UserX className="h-5 w-5" />
          Account Deactivation
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};